/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/alt-text */

import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import searchIcon from "../../../../images/searchIcon.png";
import "react-toastify/dist/ReactToastify.css";
import { logEvent, ITEM_CREATION_EVENTS } from "../../../../utils/amplitude";

import { Container } from "../../../../globalstyled";
import { AppCSS, Spacer, TxtInput } from "../../../../components";
import { ColDiv } from "../../../../components/misc/misc.styled";
import LeadsTableHeaderCard from "../../../../components/table/CustomTableHeaderCard";
import LeadsTableBodyCard from "../../../../components/table/CustomTableBodyCard";
import { MATERIAL_CATEGORIES_TABLE_HEADER_LIST } from "../../../../contants/dashboardHeader";
import { MaterialCategories } from "../../../../redux/types/creation";
import MaterialCategoriesDrawer from "../SideDrawer/index";
import { FormTitleTypography } from "../itemCreation.styled";
import CancelAndSubmitButtons from "../ItemCreationComponent/SubmitAndCancelButton";
import SearchAndCreateComponent from "../ItemCreationComponent/SearchAndCreateComponent";
import SideDrawerHeader from "../SideDrawer/SideDrawerHeader";
import Pagination from "../ItemCreationComponent/Pagination";
import { CreateMaterialCategoryInput } from "../../../../types/graphQlQuery.interface";
import {
  useArchiveMaterialCategory,
  useCreateMaterialCategory,
  useUpdateMaterialCategory,
} from "../../../../apolloClient/hooks/itemCreation/createHooks";
import EmptyTable from "../../../../components/table/EmptyTable";
import TableViewDetails from "../../../../components/table/TableViewDetails";
import ConfirmationDialog from "../../../../components/dialogue/ConfirmationDialog";

interface MaterialCategoriesTableProps {
  materialCategoriesData: MaterialCategories[];
  refetchMaterialCategories?: Function;
  permissions?: boolean;
}

export const MaterialCategoriesTable: React.FC<
  MaterialCategoriesTableProps
> = ({ materialCategoriesData, refetchMaterialCategories, permissions }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(8);
  const [keyword, setKeyword] = React.useState("");

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);

  const [materialCategoriesName, setMaterialCategoriesName] =
    useState<string>("");
  const [selectedCategory, setSelectedCategory] =
    React.useState<MaterialCategories | null>(null);

  const { createMaterialCategory } = useCreateMaterialCategory();
  const { updateMaterialCategory } = useUpdateMaterialCategory();
  const { archiveMaterialCategory } = useArchiveMaterialCategory();

  const [openArchieveDialog, setOpenArchieveDialog] = useState(false);
  const [selectedMaterialCategory, setSelectedMaterialCategory] =
    useState<MaterialCategories | null>(null);

  const handleArchive = (row: MaterialCategories) => {
    setSelectedMaterialCategory(row);
    setOpenArchieveDialog(true);
  };

  const confirmArchieve = async () => {
    if (selectedMaterialCategory) {
      const success = await archiveMaterialCategory(
        selectedMaterialCategory.id
      );
      if (success) {
        toast(`${selectedMaterialCategory.name} Archived!`, { type: "error" });
        logEvent(ITEM_CREATION_EVENTS.MATERIAL_CATEGORY_ARCHIVED, { category_id: selectedMaterialCategory.id, category_name: selectedMaterialCategory.name, timestamp: new Date().toISOString() });
        refetchMaterialCategories();
      } else {
        toast(`Failed to Archive ${selectedMaterialCategory.name}`);
      }
      setOpenArchieveDialog(false);
      setSelectedMaterialCategory(null);
    }
  };

  const cancelArchieve = () => {
    setOpenArchieveDialog(false);
    setSelectedMaterialCategory(null);
  };

  const handleSearchIconClick = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const filterMaterialCategories = () => {
    if (keyword.length > 0 && Array.isArray(materialCategoriesData)) {
      return materialCategoriesData.filter((item) => {
        const materialCategoryName = `${item.name}`;
        return materialCategoryName
          .toLowerCase()
          .includes(keyword.toLowerCase());
      });
    }
    return materialCategoriesData;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    setPage((prevPage) =>
      Math.min(
        prevPage + 1,
        Math.ceil(filterMaterialCategories().length / rowsPerPage) - 1
      )
    );
  };

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleEditClick = (category: MaterialCategories) => {
    setSelectedCategory(category);
    setMaterialCategoriesName(category.name);
    setOpenDrawer(true);
  };

  const onTapSubmitForm = async () => {
    const validationRules = [
      {
        condition: !materialCategoriesName,
        message: "Please enter a material categories name!",
      },
    ];

    for (const rule of validationRules) {
      if (rule.condition) {
        toast(rule.message, {
          type: "error",
          position: "top-left",
          className: "toast-message",
        });
        return;
      }
    }

    if (selectedCategory) {
      // Edit mode
      const input = {
        id: selectedCategory.id,
        name: materialCategoriesName,
      };

      const success = await updateMaterialCategory(input);
      if (success) {
        toast("Material Category updated successfully.", {
          type: "success",
          className: "toast-message",
        });
        logEvent(ITEM_CREATION_EVENTS.MATERIAL_CATEGORY_UPDATED, { category_id: selectedCategory.id, category_name: materialCategoriesName, timestamp: new Date().toISOString() });
        refetchMaterialCategories();
        handleDrawerClose();
        setMaterialCategoriesName("");
        setSelectedCategory(null);
        return;
      } else {
        console.error("Failed to update material category.");
      }
    } else {
      // Create mode
      const input: CreateMaterialCategoryInput = {
        name: materialCategoriesName,
      };


      const success = await createMaterialCategory(input);
      if (success) {
        toast("Material Category created successfully.", {
          type: "success",
          className: "toast-message",
        });
        logEvent(ITEM_CREATION_EVENTS.MATERIAL_CATEGORY_CREATED, { category_name: materialCategoriesName, timestamp: new Date().toISOString() });
        refetchMaterialCategories();
        handleDrawerClose();
        setMaterialCategoriesName("");
        return;
      } else {
        console.error("Failed to create material category.");
      }
    }
  };

  const createMiscFieldsForm = () => {
    return (
      <MaterialCategoriesDrawer open={openDrawer} onClose={handleDrawerClose}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: "100%",
          }}
        >
          <SideDrawerHeader
            handleDrawerClose={handleDrawerClose}
            labelTitle={
              selectedCategory
                ? "Edit material category"
                : "Create new material category"
            }
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: { xs: "100%", md: "97%" },
            }}
          >
            {/* Material Category Name */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#1a1a2e",
                }}
              >
                Material Category Name *
              </Typography>
              <TxtInput
                placeholder="Enter category name"
                onChange={setMaterialCategoriesName}
                required={true}
                value={materialCategoriesName}
                background="inherit"
                borderColor="rgba(173, 173, 173, 1)"
                borderRadius={10}
                height={50}
              />
            </Box>
          </Box>
        </Box>

        <CancelAndSubmitButtons
          onCancel={() => {
            handleDrawerClose();
            setSelectedCategory(null);
          }}
          onCreate={onTapSubmitForm}
          submitText={selectedCategory ? "Update" : "Create"}
        />
      </MaterialCategoriesDrawer>
    );
  };

  const MaterialCategoriesTable = () => {
    const paginatedData = filterMaterialCategories().slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    const totalPages = Math.ceil(filterMaterialCategories().length / rowsPerPage);

    return (
      <Box sx={{ width: "100%" }}>
        {/* Search and Create Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              height: "44px",
              width: { xs: "100%", md: "320px" },
              transition: "border-color 0.2s, box-shadow 0.2s",
              "&:focus-within": {
                borderColor: "#4b87ed",
                boxShadow: "0 0 0 3px rgba(75, 135, 237, 0.1)",
              },
            }}
          >
            <Box
              component="img"
              src={searchIcon}
              sx={{ width: 16, height: 16, cursor: "pointer", margin: "0 12px" }}
              onClick={handleSearchIconClick}
            />
            <TxtInput
              placeholder="Search material categories"
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </Box>

          {permissions && (
            <Box
              component="button"
              onClick={() => {
                setSelectedCategory(null);
                setMaterialCategoriesName("");
                handleDrawerOpen();
              }}
              sx={{
                padding: "10px 20px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s",
                backgroundColor: "#4b87ed",
                color: "white",
                border: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#3a6fd8",
                },
              }}
            >
              + Create new category
            </Box>
          )}
        </Box>

        {/* Table */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Desktop Table Header */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              padding: "14px 20px",
              backgroundColor: "#f3f4f6",
              borderBottom: "1px solid #e5e7eb",
              fontWeight: 600,
              fontSize: 12,
              color: "#6b7280",
              textTransform: "uppercase",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "20%", minWidth: "60px" }}>ID</Box>
            <Box sx={{ width: "50%", minWidth: "150px" }}>Name</Box>
            <Box sx={{ width: "30%", minWidth: "120px", textAlign: "right" }}>Actions</Box>
          </Box>

          {/* Table Body */}
          {filterMaterialCategories().length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 24px",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 15, color: "#6b7280", margin: 0 }}>
                No material categories available
              </Typography>
            </Box>
          ) : (
            paginatedData.map((row, rowIndex) => (
              <Box
                key={`material-category-${rowIndex}`}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "center" },
                  padding: { xs: "14px 16px", md: "14px 20px" },
                  borderBottom: "1px solid #f0f0f0",
                  gap: { xs: 1, md: 0 },
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                {/* ID */}
                <Box sx={{ width: { xs: "100%", md: "20%" }, minWidth: { md: "60px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    ID
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#1a1a2e" }}>
                    {row.id}
                  </Typography>
                </Box>

                {/* Name */}
                <Box sx={{ width: { xs: "100%", md: "50%" }, minWidth: { md: "150px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    Name
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#374151" }}>
                    {row.name}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "30%" },
                    minWidth: { md: "120px" },
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 1,
                    flexWrap: "wrap",
                    marginTop: { xs: 1, md: 0 },
                  }}
                >
                  {permissions && (
                    <>
                      <Box
                        component="button"
                        onClick={() => handleEditClick(row)}
                        sx={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          backgroundColor: "#e5e7eb",
                          color: "#374151",
                          border: "none",
                          "&:hover": {
                            backgroundColor: "#d1d5db",
                          },
                        }}
                      >
                        Edit
                      </Box>
                      <Box
                        component="button"
                        onClick={() => handleArchive(row)}
                        sx={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                          border: "none",
                          "&:hover": {
                            backgroundColor: "#fecaca",
                          },
                        }}
                      >
                        Archive
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))
          )}

          {/* Pagination */}
          {filterMaterialCategories().length > 0 && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-end" },
                padding: "14px 20px",
                gap: 1,
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
              }}
            >
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <Typography sx={{ fontSize: 14, color: "#6b7280", margin: "0 8px" }}>
                Page {page + 1} of {totalPages}
              </Typography>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const materialCategoriesContainer = () => {
    return (
      <Box sx={{ width: "100%" }}>
        {Array.isArray(materialCategoriesData) && MaterialCategoriesTable()}
        {createMiscFieldsForm()}
      </Box>
    );
  };

  return (
    <>
      {materialCategoriesContainer()}
      <ConfirmationDialog
        open={openArchieveDialog}
        title="Confirm Archive"
        message={`Are you sure you want to Archive "${selectedMaterialCategory?.name}"?`}
        onConfirm={confirmArchieve}
        onCancel={cancelArchieve}
      />
    </>
  );
};
